import {
	CurrentUserRoleLike,
} from '../UserSettings/hierarchySelection';

export interface OrganizationAdministratorOption {
	id?: number;
	name: string;
	email?: string;
}

export interface SecurityRoleLookup extends CurrentUserRoleLike {
	'@id'?: string;
}

const getResourceNumericId = (resourceId?: string | null): string | null => {
	if (!resourceId) return null;
	const last = String(resourceId).split('/').filter(Boolean).pop();
	return last || null;
};

const findRoleByResource = (
	resourceId: string | undefined,
	securityRoles: SecurityRoleLookup[]
): SecurityRoleLookup | undefined => {
	if (!resourceId) return undefined;
	return securityRoles.find((role) => {
		if (role['@id'] === resourceId) return true;
		return getResourceNumericId(role['@id']) === getResourceNumericId(resourceId);
	});
};

/**
 * Resolves a user's security role to a name/type object.
 * workplace_employees often returns an IRI only; look that up in the roles list.
 */
export const resolveUserSecurityRole = (
	user: {
		securityRole?: SecurityRoleLookup | string | null;
		_raw?: { securityRole?: SecurityRoleLookup | string | null };
		_full?: { securityRole?: SecurityRoleLookup | string | null };
	},
	securityRoles: SecurityRoleLookup[]
): CurrentUserRoleLike | null => {
	const raw =
		user?.securityRole ?? user?._full?.securityRole ?? user?._raw?.securityRole ?? null;
	if (!raw) return null;

	if (typeof raw === 'string') {
		return findRoleByResource(raw, securityRoles) ?? { name: '' };
	}

	const hasIdentity = Boolean(raw.name || raw.securityRoleType);
	if (hasIdentity) return raw;

	return findRoleByResource(raw['@id'], securityRoles) ?? raw;
};

const getUserDisplayName = (user: {
	name?: string;
	firstName?: string;
	lastName?: string;
}): string =>
	(user.name || `${user.firstName || ''} ${user.lastName || ''}`).trim();

const normalizeEmail = (email?: string | null): string => (email ?? '').trim().toLowerCase();

const isActiveNonSeparatedUser = (user: {
	separation?: boolean | null;
	userType?: { active?: boolean | null } | null;
	active?: boolean | null;
}): boolean => {
	if (user.separation) return false;
	if (user.userType?.active === false) return false;
	if (user.active === false) return false;
	return true;
};

const optionsMatch = (
	option: OrganizationAdministratorOption,
	candidate?: { id?: number; name?: string; email?: string } | null
): boolean => {
	if (!candidate) return false;
	if (option.id != null && candidate.id != null && option.id === candidate.id) return true;
	const optionEmail = normalizeEmail(option.email);
	const candidateEmail = normalizeEmail(candidate.email);
	if (optionEmail && candidateEmail && optionEmail === candidateEmail) return true;
	return Boolean(option.name && candidate.name && option.name === candidate.name);
};

/**
 * True when the user is the Organization Administrator configured on the org record.
 * Other users with the Organization Admin security role are not protected by this check.
 */
export const isDesignatedOrganizationAdministrator = (
	user?: {
		id?: number;
		name?: string;
		firstName?: string;
		lastName?: string;
		email?: string;
	} | null,
	organizationAdministrator?: { id?: number; name?: string; email?: string } | null
): boolean => {
	if (!user || !organizationAdministrator) return false;
	if (
		organizationAdministrator.id == null &&
		!normalizeEmail(organizationAdministrator.email) &&
		!organizationAdministrator.name?.trim()
	) {
		return false;
	}

	return optionsMatch(
		{
			id: user.id,
			name: getUserDisplayName(user),
			email: user.email,
		},
		organizationAdministrator
	);
};

export const organizationAdministratorsMatch = (
	a?: { id?: number; name?: string; email?: string } | null,
	b?: { id?: number; name?: string; email?: string } | null
): boolean => {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return optionsMatch({ id: a.id, name: a.name || '', email: a.email }, b);
};

/**
 * Maps organization-level users (already org-admin eligible from the API) into picker options.
 * Inactive/separated users are excluded. The current administrator is always included
 * so the selected value stays valid.
 */
export const getOrganizationAdministratorOptions = (
	users: Array<{
		id?: number;
		name?: string;
		firstName?: string;
		lastName?: string;
		email?: string;
		separation?: boolean | null;
		active?: boolean | null;
		userType?: { active?: boolean | null } | null;
	}>,
	currentAdministrator?: { id?: number; name?: string; email?: string } | null
): OrganizationAdministratorOption[] => {
	const options: OrganizationAdministratorOption[] = [];

	for (const user of users) {
		if (!isActiveNonSeparatedUser(user)) continue;

		const name = getUserDisplayName(user);
		if (!name) continue;

		options.push({
			id: user.id,
			name,
			email: user.email,
		});
	}

	const currentName = currentAdministrator?.name?.trim();
	if (currentName && !options.some((option) => optionsMatch(option, currentAdministrator))) {
		options.push({
			id: currentAdministrator?.id,
			name: currentName,
			email: currentAdministrator?.email,
		});
	}

	return options.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};

export const findOrganizationAdministratorOption = (
	options: OrganizationAdministratorOption[],
	currentAdministrator?: { id?: number; name?: string; email?: string } | null
): OrganizationAdministratorOption | null => {
	if (!currentAdministrator?.name && !currentAdministrator?.email && currentAdministrator?.id == null) {
		return null;
	}

	return options.find((option) => optionsMatch(option, currentAdministrator)) || null;
};
